import { composeRefs } from '@radix-ui/react-compose-refs';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn, useIsomorphicLayoutEffect } from '@heidi/utils';

import {
  segmentedControlContentClasses,
  segmentedControlIndicatorClasses,
  segmentedControlListVariants,
  segmentedControlTriggerVariants,
} from './segmented-control.styles';
import type {
  SegmentedControlContentProps,
  SegmentedControlListProps,
  SegmentedControlProps,
  SegmentedControlTriggerProps,
} from './segmented-control.types';

const SegmentedControl = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  SegmentedControlProps
>(({ 'data-testid': testId, className, orientation = 'horizontal', ...props }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    data-testid={testId}
    data-slot="segmented-control"
    data-orientation={orientation}
    orientation={orientation}
    className={cn(
      'group/segmented-control flex gap-2',
      orientation === 'horizontal' && 'flex-col',
      className
    )}
    {...props}
  />
));
SegmentedControl.displayName = 'SegmentedControl';

const SegmentedControlList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  SegmentedControlListProps
>(
  (
    { 'data-testid': testId, className, variant = 'default', children, ...props },
    ref
  ) => {
    const internalListRef = React.useRef<HTMLDivElement>(null);
    const indicatorRef = React.useRef<HTMLSpanElement>(null);
    // Same as the just-measured active = re-measurement (snap); different = real switch (slide).
    const prevActiveRef = React.useRef<HTMLElement | null>(null);
    // Tracks the last committed geometry — the "F" (First) reference for the next FLIP slide.
    const prevGeometryRef = React.useRef<{
      x: number;
      y: number;
      width: number;
      height: number;
    } | null>(null);

    // Isomorphic so SSR doesn't warn about useLayoutEffect on the server.
    useIsomorphicLayoutEffect(() => {
      const list = internalListRef.current;
      const indicator = indicatorRef.current;
      if (!list || !indicator) return;

      // Scoped to this effect so rapid switches don't leak overlapping timeouts.
      let willChangeTimeoutId: number | null = null;
      const scheduleWillChangeCleanup = () => {
        if (willChangeTimeoutId !== null) {
          window.clearTimeout(willChangeTimeoutId);
        }
        // 200ms slide + 40ms safety. `transitionend` isn't reliable (reduced-motion, mid-flight
        // interrupt), and we don't want idle indicators holding a GPU layer.
        willChangeTimeoutId = window.setTimeout(() => {
          indicator.style.willChange = '';
          willChangeTimeoutId = null;
        }, 240);
      };

      type Geometry = { x: number; y: number; width: number; height: number };

      const snap = (geo: Geometry) => {
        // Instant write with no transition; restored at the end so the next mutation (a real
        // switch) can animate via the base class transition.
        indicator.style.transition = 'none';
        indicator.style.width = `${geo.width}px`;
        indicator.style.height = `${geo.height}px`;
        indicator.style.transform = `translate(${geo.x}px, ${geo.y}px)`;
        indicator.style.opacity = '1';
        indicator.style.willChange = '';
        // Force commit so the transition restore below doesn't batch with this snap.
        indicator.getBoundingClientRect();
        indicator.style.transition = '';
      };

      const slide = (prev: Geometry, next: Geometry) => {
        // FLIP morph. Set new size + an inverse transform so the indicator visually starts at
        // `prev`, reflow, then animate transform back to identity — position and size interpolate
        // together on the compositor. With `transform-origin: 0 0`, the invert math lands
        // top-left at (prev.x, prev.y) and bottom-right at (prev.x + prev.width, prev.y + prev.height).
        indicator.style.transition = 'none';
        indicator.style.width = `${next.width}px`;
        indicator.style.height = `${next.height}px`;
        const dx = prev.x - next.x;
        const dy = prev.y - next.y;
        const sx = prev.width / next.width;
        const sy = prev.height / next.height;
        indicator.style.transform = `translate(${next.x + dx}px, ${next.y + dy}px) scale(${sx}, ${sy})`;
        indicator.style.opacity = '1';
        indicator.style.willChange = 'transform';
        // Reflow so the inverted state commits before the play step animates back.
        indicator.getBoundingClientRect();
        indicator.style.transition = '';
        indicator.style.transform = `translate(${next.x}px, ${next.y}px)`;
        scheduleWillChangeCleanup();
      };

      const update = () => {
        const active = list.querySelector<HTMLElement>('[data-state="active"]');
        if (!active) {
          prevActiveRef.current = null;
          prevGeometryRef.current = null;
          indicator.style.opacity = '0';
          return;
        }
        const next: Geometry = {
          x: active.offsetLeft,
          y: active.offsetTop,
          width: active.offsetWidth,
          height: active.offsetHeight,
        };
        // Active trigger has no layout (responsive `hidden`, collapsed parent, pre-paint, etc.) —
        // treat as no-active so the next slide can't divide by zero in the scale math. Once the
        // trigger gets real geometry, the next observer fire treats it as a snap.
        if (next.width === 0 || next.height === 0) {
          prevActiveRef.current = null;
          prevGeometryRef.current = null;
          indicator.style.opacity = '0';
          return;
        }
        const prev = prevGeometryRef.current;
        // Slide only on a real active-element change; first paint and re-measurements snap.
        // Edge case: a ResizeObserver fire mid-slide (window resize, sidebar toggle) flips back
        // to snap and truncates the transition. Acceptable — the slide is compositor-only so it
        // doesn't itself trigger RO.
        const isRealSwitch =
          prevActiveRef.current !== null && prevActiveRef.current !== active;
        prevActiveRef.current = active;
        if (isRealSwitch && prev) {
          slide(prev, next);
        } else {
          snap(next);
        }
        prevGeometryRef.current = next;
      };

      update();

      const mo = new MutationObserver(update);
      mo.observe(list, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state'],
      });

      // List is `w-fit`, so trigger resizes propagate to the list and fire this — no per-trigger observation needed.
      const ro = new ResizeObserver(update);
      ro.observe(list);

      return () => {
        mo.disconnect();
        ro.disconnect();
        if (willChangeTimeoutId !== null) {
          window.clearTimeout(willChangeTimeoutId);
        }
      };
    }, []);

    return (
      <TabsPrimitive.List
        ref={composeRefs(ref, internalListRef)}
        data-testid={testId}
        data-slot="segmented-control-list"
        data-variant={variant}
        className={cn(segmentedControlListVariants({ variant }), className)}
        {...props}
      >
        <span
          ref={indicatorRef}
          aria-hidden="true"
          data-slot="segmented-control-indicator"
          className={segmentedControlIndicatorClasses}
          // Hidden on mount; the layout effect flips opacity to 1 after the first measurement
          // so on-demand mounts (popovers, modals) don't flash from `translate(0, 0)`.
          style={{ opacity: 0 }}
        />
        {children}
      </TabsPrimitive.List>
    );
  }
);
SegmentedControlList.displayName = 'SegmentedControlList';

const SegmentedControlTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  SegmentedControlTriggerProps
>(({ 'data-testid': testId, className, iconOnly = false, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    data-testid={testId}
    data-slot="segmented-control-trigger"
    className={cn(segmentedControlTriggerVariants({ iconOnly }), className)}
    {...props}
  />
));
SegmentedControlTrigger.displayName = 'SegmentedControlTrigger';

const SegmentedControlContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  SegmentedControlContentProps
>(({ 'data-testid': testId, className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    data-testid={testId}
    data-slot="segmented-control-content"
    className={cn(segmentedControlContentClasses, className)}
    {...props}
  />
));
SegmentedControlContent.displayName = 'SegmentedControlContent';

export {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
};
